import { Avatar, Button, Flex, MenuTrigger, ModalTrigger, Text } from "@hopper-ui/components";
import { AngleDownIcon } from "@hopper-ui/icons";
import { navigateTo } from "@sg-protect/core";
import { type ReactNode, type RefObject, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { getHomePaths } from "../../../homePaths.ts";
import { useShellTranslation } from "../../../i18n/i18next.ts";
import { useSession } from "../../../SessionProvider.tsx";
import { useWorkspaceContext } from "../../../WorkspaceContext.tsx";
import { cleanupDisplayName } from "./cleanupDisplayName.ts";
import { SwitcherTipPopover } from "./SwitcherTipPopover.tsx";
import { WorkspaceSwitcherMenu } from "./WorkspaceSwitcherMenu.tsx";
import { WorkspaceSwitcherModal } from "./WorkspaceSwitcherModal.tsx";

interface WorkspaceModalSwitcherProps {
    children: (props: { isOpen: boolean }) => ReactNode;
    onCreateWorkspace?: () => void;
}

function WorkspaceModalSwitcher({ children, onCreateWorkspace }: WorkspaceModalSwitcherProps) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <ModalTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
            {children({ isOpen })}
            <WorkspaceSwitcherModal onCreateWorkspace={onCreateWorkspace} />
        </ModalTrigger>
    );
}

interface WorkspaceDropdownMenuProps {
    children: (props: { isOpen: boolean }) => ReactNode;
    onCreateWorkspace?: () => void;
}

function WorkspaceDropdownMenu({ children, onCreateWorkspace }: WorkspaceDropdownMenuProps) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <MenuTrigger align="end" isOpen={isOpen} onOpenChange={setIsOpen}>
            {children({ isOpen })}
            <WorkspaceSwitcherMenu onCreateWorkspace={onCreateWorkspace} />
        </MenuTrigger>
    );
}

interface WorkspaceTriggerButtonProps {
    name: string;
    isOpen: boolean;
    triggerRef?: RefObject<HTMLButtonElement | null>;
}

export function WorkspaceTriggerButton({ name, isOpen, triggerRef }: WorkspaceTriggerButtonProps) {
    const { t } = useShellTranslation();

    return (
        <Button
            ref={triggerRef}
            variant="ghost-secondary"
            UNSAFE_maxWidth="20rem"
            UNSAFE_gap="0.75rem"
            aria-label={t("WorkspaceMenu.trigger", { workspaceName: name })}
        >
            <Avatar borderRadius="rounded-md" name={cleanupDisplayName(name)} />
            <Text
                display={{ base: "none", sm: "inline" }}
                fontWeight="heading-xs-medium"
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                color="neutral-weak"
            >
                {name}
            </Text>
            <AngleDownIcon
                size="sm"
                color="neutral-weak"
                slot="end-icon"
                transition="transform 0.2s ease"
                transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
            />
        </Button>
    );
}

export function WorkspaceMenu() {
    const { currentWorkspace, workspaces } = useWorkspaceContext();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const { products } = useSession();
    const navigate = useNavigate();

    if (!currentWorkspace) {
        return null;
    }

    // The session only knows the current workspace's create access. From a client workspace, assume
    // the partner has it; the create page checks the partner's real access and redirects if not.
    const partnerWorkspace = currentWorkspace.isPartner ? currentWorkspace : workspaces.find(w => w.isPartner);
    const canCreateWorkspace =
        partnerWorkspace !== undefined &&
        (partnerWorkspace !== currentWorkspace || products?.partner?.canCreateWorkspace === true);
    const handleCreateWorkspace =
        canCreateWorkspace && partnerWorkspace
            ? () => {
                  const path = getHomePaths(partnerWorkspace.id).createClientWorkspace;

                  // A different workspace needs a full reload so the session and queries load for it.
                  if (partnerWorkspace === currentWorkspace) {
                      void navigate(path);
                  } else {
                      navigateTo(path);
                  }
              }
            : undefined;

    const mode = workspaces.length <= 1 ? "single" : workspaces.length <= 10 ? "dropdown" : "modal";

    if (mode === "single") {
        return (
            <Flex alignItems="center" gap="inline-sm" padding="inset-xs" UNSAFE_maxWidth="20rem">
                <Avatar borderRadius="rounded-md" name={cleanupDisplayName(currentWorkspace.name)} />
                <Text
                    display={{ base: "none", sm: "inline" }}
                    fontWeight="heading-xs-medium"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    textOverflow="ellipsis"
                >
                    {currentWorkspace.name}
                </Text>
            </Flex>
        );
    }

    const switcherTip = (
        <SwitcherTipPopover
            triggerRef={triggerRef}
            workspaceId={currentWorkspace.id}
            workspaceName={currentWorkspace.name}
        />
    );

    if (mode === "modal") {
        return (
            <>
                <WorkspaceModalSwitcher onCreateWorkspace={handleCreateWorkspace}>
                    {({ isOpen }) => (
                        <WorkspaceTriggerButton name={currentWorkspace.name} isOpen={isOpen} triggerRef={triggerRef} />
                    )}
                </WorkspaceModalSwitcher>
                {switcherTip}
            </>
        );
    }

    return (
        <>
            <WorkspaceDropdownMenu onCreateWorkspace={handleCreateWorkspace}>
                {({ isOpen }) => (
                    <WorkspaceTriggerButton name={currentWorkspace.name} isOpen={isOpen} triggerRef={triggerRef} />
                )}
            </WorkspaceDropdownMenu>
            {switcherTip}
        </>
    );
}
